import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { log } from "../../lib/logger";

const KIND_COLOR = {
  veteran_housing: "#D4AF37",
  mixed_use: "#C0C0C0",
  workforce: "#D4AF37",
  land_dev: "#9CA3AF",
  club: "#E6B800",
  education: "#60A5FA",
  redevelopment: "#FCA5A5",
  service_area: "#FFFFFF",
  parcel: "#D4AF37",
};

const DARK_TILE = "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png";
const DARK_LABELS = "https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png";
const ATTR = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · © <a href="https://carto.com/attributions">CARTO</a>';

function GoldMarker({ lat, lng, color, label, note, large = false }) {
  return (
    <>
      <CircleMarker center={[lat, lng]} radius={large ? 22 : 14} pathOptions={{ color, fillColor: color, fillOpacity: 0.08, weight: 1, opacity: 0.5 }} />
      <CircleMarker center={[lat, lng]} radius={large ? 8 : 5} pathOptions={{ color, fillColor: color, fillOpacity: 1, weight: 0 }}>
        <Tooltip direction="top" offset={[0, -8]} className="lf-tip" opacity={1}>
          <div style={{ fontFamily: "Unbounded, sans-serif", color: "#fff", fontSize: 12 }}>{label}</div>
          <div style={{ color: "#a1a1aa", fontSize: 11 }}>{note}</div>
        </Tooltip>
        <Popup>
          <div style={{ fontFamily: "Unbounded, sans-serif", color: "#111", fontSize: 13, fontWeight: 600 }}>{label}</div>
          <div style={{ color: "#444", fontSize: 12, marginTop: 4 }}>{note}</div>
        </Popup>
      </CircleMarker>
    </>
  );
}

export function PublicMap({ height = 480 }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/map/public").then((r) => setItems(r.data.items)).catch((e) => log.warn("public map fetch failed", e?.message)); }, []);
  return (
    <div style={{ height }} className="relative border border-white/10 overflow-hidden bg-[#0d0d10]" data-testid="public-map">
      <MapContainer center={[32.466, -84.99]} zoom={12} scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#0A0A0A" }}>
        <TileLayer url={DARK_TILE} attribution={ATTR} />
        <TileLayer url={DARK_LABELS} attribution="" />
        {items.map((p) => (
          <GoldMarker key={p.id} lat={p.lat} lng={p.lng} color={KIND_COLOR[p.kind] || "#D4AF37"} label={p.label} note={p.note} />
        ))}
      </MapContainer>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/0 via-transparent to-[#0A0A0A]/60" />
      <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.25em] text-white/40 bg-black/60 px-2 py-1 backdrop-blur">
        Vision · Growth · Community Impact
      </div>
    </div>
  );
}

export function InvestorMap({ height = 520 }) {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/map/investor").then((r) => setItems(r.data.items)).catch((e) => setErr(e?.response?.data?.detail || "Access denied"));
  }, []);
  if (err) return <div className="border border-white/10 p-8 text-sm text-white/70">{err} — requires admin or investor role.</div>;
  return (
    <div style={{ height }} className="relative border border-gold/30 overflow-hidden bg-[#0d0d10]" data-testid="investor-map">
      <MapContainer center={[32.467, -84.995]} zoom={13} scrollWheelZoom style={{ height: "100%", width: "100%", background: "#0A0A0A" }}>
        <TileLayer url={DARK_TILE} attribution={ATTR} />
        <TileLayer url={DARK_LABELS} attribution="" />
        {items.map((p) => (
          <GoldMarker key={p.id} lat={p.lat} lng={p.lng} color="#D4AF37" large
            label={`${p.id.toUpperCase()} · ${p.phase}`}
            note={`${p.acres} ac · ${p.zoning} · ${p.status} · ${p.milestone}`}
          />
        ))}
      </MapContainer>
      <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.3em] text-gold bg-black/70 px-3 py-1.5 border border-gold/30">
        Confidential · Accredited Investors Only
      </div>
    </div>
  );
}
